import { APButton, APIconButton, APTypography } from '@shared/components/atoms';
import { useTranslate } from '@shared/hooks/use-translate';
import {
  useAppVersionStore,
  useIsAppVersionBannerVisible,
} from '@shared/stores/use-app-version-store';

import type { FC } from 'react';

import './styles.scss';

/**
 * "A new version is available" — a thin, dismissable line under the header and above the content.
 *
 * Not in the header itself: the header is for controls, and a notice squeezed between them is easy to
 * read as one more button. Not floating either: a fixed corner control is where a table puts its
 * pager, which the feedback widget already learned the hard way. The default layout places this bar
 * directly under the fixed header and pushes the content down by its height while it shows, so it
 * never covers anything.
 *
 * Closing it hides it for that build only; a newer deploy shows it again. The refresh action is a
 * text button, never primary — the view's own primary action keeps that role. The state comes from
 * `AppVersionCheck`, which does the polling.
 */
export const AppVersionBanner: FC = () => {
  const translate = useTranslate();
  const isVisible = useIsAppVersionBannerVisible();
  const dismiss = useAppVersionStore((state) => state.dismiss);

  return (
    // The live region stays mounted while empty: one added together with its text is often not
    // announced, so it must already exist when the notice appears.
    <div className="app-version-banner" role="status" aria-live="polite">
      {isVisible && (
        <div className="app-version-banner__bar" data-testid="app-version-available">
          <APTypography size="small">
            {translate(
              'appVersion.available',
              undefined,
              'A new version of the panel is available.'
            )}
          </APTypography>
          <APButton variant="text" size="small" onClick={() => window.location.reload()}>
            {translate('appVersion.refresh', undefined, 'Refresh now')}
          </APButton>
          <APIconButton
            icon="close"
            size="small"
            variant="transparent"
            aria-label={translate('appVersion.dismiss', undefined, 'Dismiss')}
            onClick={dismiss}
            className="app-version-banner__dismiss"
          />
        </div>
      )}
    </div>
  );
};

AppVersionBanner.displayName = 'AppVersionBanner';

export default AppVersionBanner;
